import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ApiService } from 'src/app/api.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { ThemingService } from 'src/app/theming.service';
import { SettingsService } from 'src/app/settings.service';
import { environment } from 'src/environments/environment';
import { LambdaService } from 'src/app/lambda.service';

declare var $: any;

@Component({
  selector: 'app-create-category',
  templateUrl: './create-category.component.html',
  styleUrls: ['./create-category.component.scss']
})
export class CreateCategoryComponent implements OnInit {

  @Output() categoryCreated = new EventEmitter<void>();

  category = {
    categoryParentId: 0 as any,
    name: '',
    activeIcon: '',
    nonActiveIcon: '',
    position: 0,
    menuCategory: 0 as any,
    homeCategory: 0 as any,
    categoryDesignType: 1,
    categoryImageUrl: '',
    isForVerifiedClient: 0,
    timekitResourceId: 0,
    timekitProjectId: 0
  }

  selectedProject: { projectId: number, resourceId: number } = { projectId: 0, resourceId: 0 };

  saveCategory = false;
  categories: any[] = [];
  imageUrl: SafeUrl | null = null; // Previsualización de la imagen
  timekitProjects:  any[] = [];

  constructor(
    private apiService: ApiService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private route: ActivatedRoute,
    public themingService: ThemingService,
    private settingsService: SettingsService,
    private lambdaService: LambdaService
  ) { }

  ngOnInit(): void {
    this.saveCategory = false;
    this.getCategories();
  }

  async getCategories() {
    try {
      const response: any = await this.lambdaService.getCategories(this.themingService.shopId);

      const categories = Array.isArray(response[0]) ? response[0] : [];
      const timekitProjects = Array.isArray(response[1]) ? response[1] : [];

      // Asignar proyecto a cada categoría
      categories.forEach(category => {
        const project = timekitProjects.find((p: any) =>
          p.timekitProjectId === category.timekitProjectId
        );
        category.projectName = project ? project.projectName : 'No Project';
      });

      this.categories = categories;
      this.timekitProjects = timekitProjects;

    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
    }
  }

  changeCategoryName(event: Event) {
    const element = event.target as HTMLInputElement;
    this.category.name = element.value;
  }

  triggerFileInput() {
    const fileInput = document.getElementById('optionImage') as HTMLInputElement;
    fileInput.click();
  }

  private resizeImage(image: HTMLImageElement, maxWidth: number, maxHeight: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    let width = image.width;
    let height = image.height;

    if (width > height) {
      if (width > maxWidth) {
        height *= maxWidth / width;
        width = maxWidth;
      }
    } else {
      if (height > maxHeight) {
        width *= maxHeight / height;
        height = maxHeight;
      }
    }

    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    context?.drawImage(image, 0, 0, width, height);

    return canvas;
  }

  private convertCanvasToFile(canvas: HTMLCanvasElement, fileName: string): File {
    const dataURL = canvas.toDataURL('image/jpeg');
    const base64Data = dataURL.split(',')[1];
    const byteCharacters = atob(base64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: 'image/jpeg' });
    return new File([blob], fileName);
  }

  async onImageChange(event: any) {
    const file: File = event.target.files[0];
    if (file) {
      const reader = new FileReader();

      reader.onload = async (e: any) => {
        const image = new Image();
        image.src = e.target.result;
        this.imageUrl = this.sanitizer.bypassSecurityTrustUrl(image.src);
        image.onload = async () => {
          const resizedImage = this.resizeImage(image, 1400, 250);
          const resizedFile = this.convertCanvasToFile(resizedImage, file.name);

          try {
            const imageData = await this.uploadImage(resizedFile);
            this.category.categoryImageUrl = imageData.imageURL;
          } catch (error) {
            console.error('Error uploading image:', error);
          }
        };
      };

      reader.readAsDataURL(file);
    }
  }

  removeImage() {
    this.imageUrl = null;
    this.category.categoryImageUrl = ''; // Limpiar la URL de la imagen en el objeto categoría
    const fileInput = document.getElementById('optionImage') as HTMLInputElement;
    fileInput.value = ''; // Resetear el campo de archivo
  }


  uploadImage(image: File): Promise<any> {
    return this.blobFile(image)
      .then((res: any) => {
        const timestamp = Date.now();
        const ext = image.name.split('.').pop();
        const key = `${this.themingService.shopId}/${environment.stage}/images/category/banner${timestamp}.${ext}`;
        return this.lambdaService.uploadFile(key, res.blob, { position: 1, type: 'original' })
          .then((data: any) => {
            return {
              imageURL: data.imageURL,
              blob: res.blob,
            };
          });
      });
  }

  blobFile = async ($event: any): Promise<any> => {
    try {
      const unSafeImg = window.URL.createObjectURL($event);
      const img = this.sanitizer.bypassSecurityTrustUrl(unSafeImg);
      const reader = new FileReader();

      return new Promise((resolve, reject) => {
        reader.onload = () => {
          resolve({
            blob: $event,
            img,
            base: reader.result
          });
        };
        reader.onerror = (error) => {
          reject({
            blob: $event,
            img,
            base: null
          });
        };
        reader.readAsDataURL($event);
      });
    } catch (e) {
      return Promise.reject(null);
    }
  };

  onResourceChange(event: any) {
    this.category.timekitProjectId = this.selectedProject.projectId;
    this.category.timekitResourceId = this.selectedProject.resourceId;
  }

  validateFormData() {
    if (!this.category.name || this.category.name.trim() === '') {
      $('#requiredCategoryModal').modal('show');
      return;
    }
    this.createCategory();
  }

  createCategory() {
    this.category.isForVerifiedClient = this.category.isForVerifiedClient ? 1 : 0;
    $('#categoryLoadingModal').modal({ backdrop: 'static', keyboard: false });
    $('#categoryLoadingModal').modal('show');
    this.saveCategory = false;
    this.lambdaService.createCategory(
      this.category.categoryParentId,
      this.themingService.shopId,
      this.category.name,
      this.category.activeIcon,
      this.category.nonActiveIcon,
      this.category.position,
      this.category.menuCategory,
      this.category.homeCategory,
      this.category.categoryDesignType,
      this.category.categoryImageUrl,
      this.category.isForVerifiedClient,

    ).then((data: any) => {
      if (data.status === 200) {
        $('#categoryLoadingModal').modal('hide');
        $('#successCategoryModal').modal('show');
        this.saveCategory = true;
        this.resetForm();
        setTimeout(() => {
          this.categoryCreated.emit();
          this.closeModal();
        }, 2000);
      } else {
        $('#errorCategoryModal').modal('show');
        $('#categoryLoadingModal').modal('hide');
      }
    }).catch((error) => {
      console.error(error);
      $('#errorCategoryModal').modal('show');
      $('#categoryLoadingModal').modal('hide');
    });
  }

  resetForm() {
    this.category.name = '';
    this.category.position = 0;
    this.category.menuCategory = 0;
    this.category.homeCategory = 0;
    this.category.activeIcon = '';
    this.category.nonActiveIcon = '';
    this.imageUrl = null; // Resetear la previsualización
  }

  closeModal() {
    $('#successCategoryModal').modal('hide');
    $('#errorCategoryModal').modal('hide');
    $('#requiredCategoryModal').modal('hide');
    this.resetForm();
    this.saveCategory = false;
  }

  setListingCategories(){
    this.settingsService.setListingCategories();
  }
}
